import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Save, DollarSign } from 'lucide-react';
import { api } from '@/services/api';
import type { BudgetSettings as BudgetSettingsType } from '@/types';
import { lightImpact, notificationSuccess } from '@/utils/haptics';
import toast from 'react-hot-toast';

const BudgetSettings = () => {
  const [settings, setSettings] = useState<BudgetSettingsType>({
    globalPool: 100,
    defaultRolePercentages: {
      admin: 50,
      adult: 30,
      teen: 15,
      child: 5,
    },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await api.getBudgetSettings();
        setSettings(data);
      } catch (err) {
        console.error('Failed to load budget settings');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateBudgetSettings(settings);
      toast.success('Budget settings saved');
      await notificationSuccess();
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
          className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  const roles = [
    { key: 'admin', label: 'Admin', color: 'from-blue-500 to-blue-600', icon: '👤' },
    { key: 'adult', label: 'Adult', color: 'from-purple-500 to-purple-600', icon: '🧑' },
    { key: 'teen', label: 'Teen', color: 'from-amber-500 to-amber-600', icon: '🧒' },
    { key: 'child', label: 'Child', color: 'from-emerald-500 to-emerald-600', icon: '👶' },
  ];

  const totalPercentage = Object.values(settings.defaultRolePercentages).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-900 dark:text-white tracking-tight">Budget Settings</h2>
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={async () => { await lightImpact(); handleSave(); }}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl gradient-bg text-white text-sm font-semibold
                     shadow-glow min-h-12 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save'}
        </motion.button>
      </div>

      {/* Global Pool */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-5 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-card"
      >
        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-3 uppercase tracking-wider">
          Global Daily Pool
        </label>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg">
            <DollarSign className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <input
              type="number"
              value={settings.globalPool}
              onChange={(e) => setSettings({ ...settings, globalPool: Number(e.target.value) })}
              min={1}
              className="w-full px-4 py-3 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100
                         focus:outline-none focus:ring-2 focus:ring-primary-500/30 text-lg font-bold"
            />
            <p className="text-xs text-slate-400 mt-1">Total daily budget across all family members</p>
          </div>
        </div>
      </motion.div>

      {/* Role Percentages */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="p-5 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-card"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200">Default Role Percentages</h3>
          <span className={`text-xs font-bold ${totalPercentage > 100 ? 'text-red-500' : 'text-emerald-500'}`}>
            Total: {totalPercentage}%
          </span>
        </div>

        <div className="space-y-5">
          {roles.map((role) => (
            <div key={role.key} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{role.icon}</span>
                  <span className="text-sm font-bold text-slate-700 dark:text-slate-200">{role.label}</span>
                </div>
                <span className="text-sm font-bold text-slate-900 dark:text-white">
                  {settings.defaultRolePercentages[role.key] || 0}%
                </span>
              </div>
              <input
                type="range"
                min={1}
                max={100}
                value={settings.defaultRolePercentages[role.key] || 0}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    defaultRolePercentages: {
                      ...settings.defaultRolePercentages,
                      [role.key]: Number(e.target.value),
                    },
                  })
                }
                className="w-full h-2 rounded-full appearance-none bg-slate-200 dark:bg-slate-700 accent-primary-600"
              />
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default BudgetSettings;
